import React, { useEffect, useMemo, useState } from "react";
import styles from "../../../../styles/scope3/Dashboard/purchaseGoods.module.css";
import PeriodFilter from "../../../../Components/Common/PeriodFilter";
import { get } from "../../../../utils/api";
import { fixedTons, formatNumberCommaSeparated } from "../../../../utils/common";
import LineChart from "../../../../Components/GraphNCharts/LineChart";
import BarChart from "../../../../Components/GraphNCharts/BarChart";
import EchartDoughnutChart from "../../../../Components/GraphNCharts/EchartDoughnut";
import NoDataFound from "../../../../Components/Masters/NoDataFound";

const eolDisposalMethodFromItem = (item) => {
  const pm =
    item?.productMaster && typeof item.productMaster === "object"
      ? item.productMaster
      : null;
  return (
    item?.endOfLifeTreatmentMethod ||
    pm?.disposalMethod ||
    item?.disposalMethod ||
    item?.treatmentMethod ||
    "Other"
  );
};

const Dashboard = () => {
  const currentYear = new Date().getFullYear();
  const [selectedFacility, setSelectedFacility] = useState([]);
  const [fromDate, setFromDate] = useState({ month: 4, year: currentYear - 1 });
  const [toDate, setToDate] = useState({
    month: 3,
    year: currentYear,
  });
  const [totalEmission, setTotalEmission] = useState(0);
  const [dashboardRecords, setDashboardRecords] = useState([]);
  const [graphRecords, setGraphRecords] = useState([]);
  const [selectedGraphYear, setSelectedGraphYear] = useState(String(currentYear));

  const yearList = useMemo(() => {
    const years = [];
    for (let year = 2020; year <= currentYear; year += 1) years.push(year);
    return years;
  }, [currentYear]);

  const getNumeric = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const n = parseFloat(value);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  };

  const fetchDashboardData = async () => {
    try {
      const start = new Date(
        Date.UTC(fromDate.year, fromDate.month - 1, 1, 0, 0, 0, 0)
      ).toISOString();
      const end = new Date(
        Date.UTC(toDate.year, toDate.month, 0, 23, 59, 59, 999)
      ).toISOString();

      const queryParams = {
        page: "1",
        limit: "1000",
        sortField: "createdAt",
        sortOrder: "desc",
        startDate: start,
        endDate: end,
      };

      if (Array.isArray(selectedFacility) && selectedFacility.length > 0) {
        queryParams.facilityList = selectedFacility.join(",");
      }

      const query = new URLSearchParams(queryParams).toString();

      const res = await get(`/scope3/eolTreatmentOfSoldProducts/get?${query}`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setDashboardRecords(list);

      const emissionSum = list.reduce(
        (acc, item) => acc + getNumeric(item?.totalEmission),
        0
      );
      setTotalEmission(emissionSum);
    } catch (error) {
      console.error("Error fetching EOL dashboard data:", error);
      setDashboardRecords([]);
      setTotalEmission(0);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fromDate, toDate, selectedFacility]);

  const fetchGraphData = async () => {
    try {
      const selectedYearInt = Number(selectedGraphYear);
      const start = new Date(
        Date.UTC(selectedYearInt, 0, 1, 0, 0, 0, 0)
      ).toISOString();
      const end = new Date(
        Date.UTC(selectedYearInt, 11, 31, 23, 59, 59, 999)
      ).toISOString();

      const queryParams = {
        page: "1",
        limit: "1000",
        sortField: "createdAt",
        sortOrder: "desc",
        startDate: start,
        endDate: end,
      };

      if (Array.isArray(selectedFacility) && selectedFacility.length > 0) {
        queryParams.facilityList = selectedFacility.join(",");
      }

      const query = new URLSearchParams(queryParams).toString();
      const res = await get(`/scope3/eolTreatmentOfSoldProducts/get?${query}`);
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setGraphRecords(list);
    } catch (error) {
      console.error("Error fetching EOL graph data:", error);
      setGraphRecords([]);
    }
  };

  useEffect(() => {
    if (!selectedGraphYear && yearList.length > 0) {
      setSelectedGraphYear(String(yearList[yearList.length - 1]));
    }
  }, [selectedGraphYear, yearList]);

  useEffect(() => {
    fetchGraphData();
  }, [selectedGraphYear, selectedFacility]);

  const monthLabels = useMemo(
    () => ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    []
  );

  const monthlyEmissionValues = useMemo(() => {
    const selectedYearInt = Number(selectedGraphYear);
    const totals = Array.from({ length: 12 }, () => 0);

    const parseMonthDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    graphRecords.forEach((item) => {
      const totalEmissionValue = getNumeric(item?.totalEmission);
      if (totalEmissionValue <= 0) return;

      const productionMonthDate = parseMonthDate(item?.eolMonth || item?.createdAt);
      if (!productionMonthDate) return;
      if (productionMonthDate.getFullYear() !== selectedYearInt) return;
      totals[productionMonthDate.getMonth()] += totalEmissionValue;
    });

    return totals;
  }, [graphRecords, selectedGraphYear]);

  const monthlyEmissionDataset = useMemo(
    () => [
      {
        label: "Emission",
        data: monthlyEmissionValues,
        borderColor: "#EF6C00",
        backgroundColor: "rgba(239, 108, 0, 0.15)",
        tension: 0.35,
        fill: false,
        pointRadius: 0,
      },
    ],
    [monthlyEmissionValues]
  );

  const treatmentMethodSummary = useMemo(() => {
    const methodMap = new Map();
    dashboardRecords.forEach((item) => {
      const methodName = eolDisposalMethodFromItem(item);
      const key = String(methodName).trim() || "Other";
      const prev = methodMap.get(key) || { count: 0, emission: 0 };
      methodMap.set(key, {
        count: prev.count + 1,
        emission: prev.emission + getNumeric(item?.totalEmission),
      });
    });

    return Array.from(methodMap.entries())
      .map(([name, value]) => ({
        name,
        count: value.count,
        emission: value.emission,
      }))
      .sort((a, b) => b.emission - a.emission);
  }, [dashboardRecords]);

  const treatmentColors = ["#FF4C4C", "#F5B335", "#005AC2", "#7B61FF", "#14B8A6", "#EF6C00"];
  const disposalSplitByEmission = treatmentMethodSummary.filter(
    (item) => getNumeric(item.emission) > 0
  );
  const disposalSplitLabels = disposalSplitByEmission.map((item) => item.name);
  const disposalSplitValues = disposalSplitByEmission.map((item) => item.emission);
  const disposalSplitTotal = disposalSplitValues.reduce(
    (sum, val) => sum + getNumeric(val),
    0
  );

  const emissionByMethodDataset = useMemo(
    () => [
      {
        label: "Emission",
        data: treatmentMethodSummary.map((item) => item.emission),
        backgroundColor: "rgba(46, 119, 228, 0.95)",
        borderColor: "rgba(46, 119, 228, 1)",
        borderWidth: 1,
      },
    ],
    [treatmentMethodSummary]
  );

  const formatThreeDecimals = (value) => Number(value || 0).toFixed(3);

  return (
    <>
      <div className={styles.heading_container}>
        <p className={styles.heading}>End of Life Treatment of Sold Products</p>
        <PeriodFilter
          {...{
            fromDate,
            setFromDate,
            toDate,
            setToDate,
            yearList,
            setSelectedFacility,
            selectedFacility,
          }}
        />
      </div>

      <div className={styles.purchase_container} style={{ paddingTop: "0.5rem" }}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <p className={styles.graph_heading}>Total Emission (tCO2e)</p>
          <p className={styles.graph_value}>
            {formatNumberCommaSeparated(fixedTons(totalEmission))}
          </p>
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.card_container} style={{ width: "100%" }}>
          <div
            className={styles.graph_container_header}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <p className={styles.graph_heading}>Monthly Emissions from Product End-of-Life Treatment</p>
            <select
              value={selectedGraphYear}
              onChange={(e) => setSelectedGraphYear(e.target.value)}
              className={styles.heading_select_year}
              style={{ minWidth: "96px" }}
            >
              {yearList.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <LineChart
            dataset={monthlyEmissionDataset}
            labels={monthLabels}
            customHeight="320px"
            display={false}
            marginTop="1rem"
            yAxisTitle="Emission (tCO2e)"
          />
        </div>
      </div>

      <div className={styles.purchase_container}>
        <div className={styles.emission_container}>
          <div className={styles.card_container}>
            <div className={styles.graph_container_header}>
              <p className={styles.graph_heading}>Percentage Emission Contribution by Disposal Method</p>
            </div>
            {disposalSplitLabels.length > 0 && disposalSplitTotal > 0 ? (
              <>
                <div className={styles.doughnut_container}>
                  <EchartDoughnutChart
                    labels={disposalSplitLabels}
                    dataPoints={disposalSplitValues}
                    colors={treatmentColors.slice(0, disposalSplitLabels.length)}
                    customWidth={"240px"}
                    customHeight={"240px"}
                    hide={true}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: "1rem 1.5rem",
                    marginTop: "0.75rem",
                    width: "100%",
                  }}
                >
                    {disposalSplitByEmission.map((item, index) => (
                        <div
                          key={item.name}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                          }}
                        >
                          <span
                            style={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor:
                                treatmentColors[index % treatmentColors.length],
                              flexShrink: 0,
                            }}
                          />
                          <span className={styles.table_text}>{item.name}</span>
                        </div>
                    ))}
                </div>
              </>
            ) : (
              <NoDataFound mt={"30%"} />
            )}
          </div>

          <div className={styles.card_container}>
            <div className={styles.graph_container_header}>
              <p className={styles.graph_heading}>
                Emissions of Waste by Treatment Method
              </p>
            </div>
            <BarChart
              labels={treatmentMethodSummary.map((item) => item.name)}
              datasets={emissionByMethodDataset}
              customHeight="280px"
              display={false}
              yAxisTitle="Emission (tCO2e)"
              linearTickFormatter={formatThreeDecimals}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
